import Axios from "axios";
import React, { useMemo, useState } from "react";
import { NotificationManager } from "react-notifications";
import signupIconSrc from "../../assets/images/signup-icon.svg";
import { Button, Footer, Header } from "../../components";
import useLocalStorageCountdown from "../../hooks/use-local-storage-countdown";
import ENDPOINTS from "../../utils/endpoints";
import styles from "./sign-up.module.css";
import ROUTES from "../../utils/routes";
import { useHistory } from "react-router-dom";
import { RATE_LIMIT_EXCEEDED } from "../../utils/api-error-types";
import { suppressErrorNotificationForTypes } from "../../utils/axios-error-notification";
import {
    AUTH_EMAIL_COOLDOWN_SECONDS,
    getRetryAfterSeconds,
    isRateLimitError,
} from "../../utils/auth-email-rate-limit";

interface ResendConfirmationProps {
    email: string;
    localizationDictionary: { [key: string]: string };
}

const confirmationResendTimerKey = (email: string) =>
    `confirmationResendTimerEnd:${email.trim().toLowerCase()}`;

const ResendConfirmation: React.FunctionComponent<ResendConfirmationProps> = (
    props: ResendConfirmationProps
) => {
    const { email, localizationDictionary } = props;
    const { push } = useHistory();
    const [loading, setLoading] = useState(false);
    const timerStorageKey = useMemo(() => confirmationResendTimerKey(email), [
        email,
    ]);
    const {
        secondsRemaining: resendCooldown,
        start: startResendCooldown,
    } = useLocalStorageCountdown(timerStorageKey, 0);

    const resendConfirmation = async () => {
        if (resendCooldown > 0) return;

        setLoading(true);

        try {
            await Axios.put(
                ENDPOINTS.RESEND_CONFIRMATION,
                { email },
                suppressErrorNotificationForTypes([RATE_LIMIT_EXCEEDED])
            );
            NotificationManager.success(
                `${localizationDictionary.confirmationResent}${email}`
            );
            startResendCooldown(AUTH_EMAIL_COOLDOWN_SECONDS);
        } catch (error) {
            if (isRateLimitError(error)) {
                startResendCooldown(getRetryAfterSeconds(error));
                NotificationManager.error(
                    localizationDictionary.rateLimitWaitMessage
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page">
            <Header />
            <div className={`${styles.container} preauth-container`}>
                <div>
                    <div className={styles.imgHolder}>
                        <img src={signupIconSrc} />
                    </div>

                    <div>
                        <h2 className={`${styles["single-line"]}`}>
                            {localizationDictionary.accountCreated}
                        </h2>
                        <div
                            className={`${styles.subtitle} ${styles["single-line"]}`}
                        >
                            <span>{localizationDictionary.clickHere}</span>
                            <b>
                                <a
                                    className={`${styles.subtitle}`}
                                    onClick={() => push(ROUTES.LOGIN)}
                                >
                                    {localizationDictionary.loginToAccount}
                                </a>
                            </b>
                        </div>
                    </div>

                    <p>
                        {localizationDictionary.emailConfirmationMessage}{" "}
                        {localizationDictionary.confirmationResent}
                        <b>{email}</b>.
                    </p>

                    <Button
                        disabled={resendCooldown > 0}
                        loading={loading}
                        type="secondary"
                        onClick={resendConfirmation}
                    >
                        {localizationDictionary.resend}
                        {resendCooldown > 0 ? ` (${resendCooldown})` : ""}
                    </Button>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ResendConfirmation;
